<script setup lang="ts">
import { storeToRefs } from 'pinia';
import DailyCheckinDayCell from './DayCell.vue';
import DailyCheckinClaimPopup from './ClaimPopup.vue';
import { useDailyCheckinStore } from '@/stores/daily-checkin';
import { getAssetsUrlForCDN, localeLink } from '@/utils/template';

// Modal "Recompensas Diárias" (Check-in Diário — épico ENG-424). Mesma
// mecânica da roletinha e do hub: Teleport pro body, slide-up por @keyframes
// (o reset global de transition em main.scss mata `transition`), e a store
// controla a abertura sem navegar de rota.
//
// A trilha é sequencial: cada célula é um DEGRAU, não um dia do mês.
//
// Figma: file 8oInBlwexpWshczTeB5rJg → "Componentes" → "Modal" (382x571).
const store = useDailyCheckinStore();
const {
  isOpen,
  status,
  board,
  rewardViews,
  claiming,
  claimError,
  selectedReward,
} = storeToRefs(store);

// Logo e número de colunas são decisões do front. A copy do (i) já foi uma
// delas: hoje o texto é o regulamento cadastrado no CMS (`period.terms`), e o
// texto abaixo sobrou como FALLBACK — sem ele, um período publicado sem
// regulamento abriria o popover em branco.
const LOGO = '/assets/images/daily-checkin/logo.webp';
const COLUMNS = 5;
const INFO_FALLBACK =
  'As recompensas são uma trilha: a cada dia você resgata o próximo degrau. Faltar um dia não perde nenhum prêmio — só atrasa seu avanço, e o período tem data para acabar.';

// `visible` mantém o overlay montado até o slide-down terminar; `sheetOpen`
// dispara a animação.
const visible = ref(false);
const sheetOpen = ref(false);

// Popover do botão (i): regulamento do período, do CMS.
const infoOpen = ref(false);

const infoText = computed(
  () => board.value?.period?.terms?.trim() || INFO_FALLBACK,
);

// O campo é textarea no CMS, então o normal é texto puro com '\n' — e é o
// `pre-line` que transforma essas quebras em parágrafos. Mas o mesmo campo na
// roletinha às vezes chega com HTML, e aí `pre-line` viraria a indentação do
// markup em linhas vazias: quando há tag, o texto é renderizado como HTML e o
// espaçamento fica com ele.
const infoIsHtml = computed(() => /<[a-z][^>]*>/i.test(infoText.value));

useBodyScrollLock(visible);

const logoImage = computed(() => getAssetsUrlForCDN(LOGO));

const asset = (file: string) =>
  getAssetsUrlForCDN(`/assets/images/daily-checkin/${file}`);

// A largura da célula sai da equação do grid: N células + (N-1) gaps, com o
// gap valendo 6/65 da célula (proporção do Figma) →
//   grid = célula * (71N - 6) / 65
// O divisor é calculado aqui, e não no CSS, pra não depender de divisão por
// expressão com var() dentro de calc().
const frameStyle = computed(() => ({
  '--dc-cell': `calc((var(--dc-panel-w) - 32px) / ${(71 * COLUMNS - 6) / 65})`,
}));

/* ---------------- contagem regressiva ---------------- */

// `next_claim_available_at` vem do core JÁ no fuso da operação, então é só
// diferença de instantes — nada de converter fuso aqui.
const now = ref(Date.now());
let timer: ReturnType<typeof setInterval> | null = null;

const nextClaimMs = computed(() => {
  const iso = board.value?.nextClaimAvailableAt;
  const ms = iso ? Date.parse(iso) : NaN;

  return Number.isNaN(ms) ? null : ms;
});

const remainingMs = computed(() =>
  nextClaimMs.value === null ? 0 : Math.max(0, nextClaimMs.value - now.value),
);

const countdown = computed(() => {
  if (!remainingMs.value) return '';

  const total = Math.floor(remainingMs.value / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${pad(Math.floor(total / 3600))}:${pad(
    Math.floor((total % 3600) / 60),
  )}:${pad(total % 60)}`;
});

// O tique de 1s só roda quando há contagem a mostrar.
const countdownNeeded = computed(
  () => !board.value?.canClaimToday && nextClaimMs.value !== null,
);

function startTimer() {
  stopTimer();
  now.value = Date.now();
  timer = setInterval(() => {
    now.value = Date.now();
  }, 1000);
}

function stopTimer() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
}

watch(countdownNeeded, (needed) => (needed ? startTimer() : stopTimer()));

// Zerou a contagem: recarrega a trilha pra reabilitar o resgate sem o usuário
// precisar fechar e abrir o modal. Tem que ser `refresh`: o board foi buscado
// há pouco (o modal está aberto), então o cache do handler devolveria o dia de
// ontem, com o resgate ainda fechado.
watch(remainingMs, (value, previous) => {
  if (previous > 0 && value === 0) store.refresh();
});

// Overlay e painel sobem no MESMO passo de render. Antes o painel esperava um
// `nextTick` (a `<Transition>` só anima o filho que aparece DEPOIS dela montar,
// e sem isso não haveria slide-up). O `appear` compra o mesmo efeito na
// primeira renderização, então o painel começa a andar junto com o backdrop em
// vez de um quadro atrás — e esse quadro custa caro, porque é o mesmo em que o
// backdrop borrado é composto pela primeira vez.
watch(
  isOpen,
  (opened) => {
    if (opened) {
      visible.value = true;
      infoOpen.value = false;
      if (countdownNeeded.value) startTimer();
      sheetOpen.value = true;
    } else {
      sheetOpen.value = false;
    }
  },
  { immediate: true },
);

onBeforeUnmount(stopTimer);

function onClosed() {
  visible.value = false;
  infoOpen.value = false;
  stopTimer();
  store.reset();
}

function close() {
  store.close();
}

// CTA do popup (USAR). Fechar o modal ANTES de navegar não é cortesia: o painel
// é teleportado pro body, então numa navegação interna (SPA) ele continuaria
// montado por cima da rota nova. O hub do Club Fidelidade, quando está aberto
// atrás, se fecha sozinho no watch de rota dele.
//
// URL absoluta navega externo; caminho interno passa por localeLink pra herdar o
// prefixo de locale — mesma regra do CTA da roletinha.
function onUse(link: string) {
  close();

  if (/^https?:\/\//i.test(link)) {
    navigateTo(link, { external: true });

    return;
  }

  navigateTo(localeLink(link));
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="dc-overlay"
      @dragstart.prevent
    >
      <div
        class="dc-backdrop"
        :class="{ 'dc-backdrop--out': !sheetOpen }"
        @click="close"
      />

      <Transition
        appear
        name="dc-slide-up"
        @after-leave="onClosed"
      >
        <div
          v-if="sheetOpen"
          class="dc-frame"
          :style="frameStyle"
        >
          <!-- Logo sangra pra cima do painel (fica metade fora), por isso vive
               no frame e não dentro do painel, que é clipado. -->
          <img
            alt="Check-in Diário"
            class="dc-logo"
            :src="logoImage"
          />

          <div class="dc-panel">
            <button
              aria-label="Como funciona"
              class="dc-icon dc-icon--info"
              type="button"
              @click="infoOpen = !infoOpen"
            >
              <img
                alt=""
                :src="asset('info.svg')"
              />
            </button>

            <button
              aria-label="Fechar"
              class="dc-icon dc-icon--close"
              type="button"
              @click="close"
            >
              <img
                alt=""
                :src="asset('close.svg')"
              />
            </button>

            <!-- Scrim invisível: fecha o popover ao clicar fora dele (mesmo
                 recurso do dropdown ⋮ da roletinha). -->
            <div
              v-if="infoOpen"
              class="dc-info-scrim"
              @click="infoOpen = false"
            />

            <!-- Popover do (i): regulamento do período (CMS). Sem botão de
                 confirmar — fecha pelo scrim ou pelo próprio (i). O corpo é a
                 única coisa que rola, pra barriguinha da caixa não ser cortada
                 (um scroll container clipa o que passa da borda). -->
            <Transition name="dc-fade">
              <div
                v-if="infoOpen"
                class="dc-info"
              >
                <div
                  v-if="infoIsHtml"
                  class="dc-info__text"
                  v-html="infoText"
                />
                <p
                  v-else
                  class="dc-info__text dc-info__text--plain"
                >
                  {{ infoText }}
                </p>
              </div>
            </Transition>

            <div
              v-if="status === 'loading' || status === 'idle'"
              class="dc-state"
            >
              Carregando recompensas...
            </div>

            <!-- Sem período publicado: o core respondeu certo, só não há jogo
                 no ar. Sem "tentar novamente", que não tentaria nada. -->
            <div
              v-else-if="status === 'unavailable'"
              class="dc-state"
            >
              <p>Nenhuma recompensa diária disponível no momento.</p>
              <p class="dc-state__hint">
                Assim que um novo período começar, sua trilha aparece aqui.
              </p>
            </div>

            <div
              v-else-if="status === 'error'"
              class="dc-state"
            >
              <p>Não foi possível carregar as recompensas.</p>
              <button
                class="dc-retry"
                type="button"
                @click="store.refresh()"
              >
                Tentar novamente
              </button>
            </div>

            <template v-else>
              <!-- Faixa de estado do jogador: o épico deixa claro que a lista
                   sozinha não distingue "pode resgatar" de "já resgatou hoje". -->
              <p class="dc-status">
                <template v-if="board?.canClaimToday">
                  Seu resgate de hoje está liberado
                </template>
                <template v-else-if="countdown">
                  Próximo resgate em
                  <strong>{{ countdown }}</strong>
                </template>
                <template v-else>Volte amanhã para o próximo degrau</template>
              </p>

              <div
                class="dc-grid"
                :style="{
                  gridTemplateColumns: `repeat(${COLUMNS}, minmax(0, 1fr))`,
                }"
              >
                <DailyCheckinDayCell
                  v-for="reward in rewardViews"
                  :key="reward.position"
                  :reward="reward"
                  @select="store.openReward"
                />
              </div>
            </template>
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>

  <!-- Popup do prêmio / resgate, em overlay próprio por cima do calendário. -->
  <DailyCheckinClaimPopup
    :claiming="claiming"
    :error="claimError"
    :reward="selectedReward"
    @claim="store.claim()"
    @close="store.closeReward()"
    @use="onUse"
  />
</template>

<!-- Sem scope: @keyframes contornam o reset global de transition (mesma
     estratégia da roletinha e do hub). -->
<style>
.dc-slide-up-enter-active {
  animation: dc-slide-up 0.32s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: transform, opacity;
}
.dc-slide-up-leave-active {
  animation: dc-slide-up 0.24s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
  will-change: transform, opacity;
}
@keyframes dc-slide-up {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

.dc-fade-enter-active {
  animation: dc-fade 0.16s ease-out forwards;
}
.dc-fade-leave-active {
  animation: dc-fade 0.12s ease-in reverse forwards;
}
@keyframes dc-fade {
  0% {
    transform: translateY(-6px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

<style scoped lang="scss">
// Empilha ACIMA do hub Club Fidelidade (z-9990) e do CashbackSheet (z-9995),
// abaixo da roletinha (z-9999) — os dois nunca aparecem juntos.
.dc-overlay {
  @apply fixed inset-0 z-[9996] flex flex-col justify-end select-none;

  img {
    -webkit-user-drag: none;
  }
}

// SEM `backdrop-filter` de propósito, ao contrário do hub e da roletinha: este
// modal quase sempre abre POR CIMA do hub, que já borra a página. Seriam dois
// blurs de tela cheia compostos ao mesmo tempo, e o quadro mais caro é
// justamente o primeiro — o do clique. O preto a 72% sozinho separa o painel do
// fundo; o que se perde é borrar a arte do hub atrás, que já está escurecida.
.dc-backdrop {
  @apply absolute inset-0;

  background: rgba(0, 0, 0, 0.72);
  animation: dc-fade-in 0.25s ease forwards;
}

.dc-backdrop--out {
  animation: dc-fade-in 0.24s ease reverse forwards;
}

@keyframes dc-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// Bottom-sheet colado no rodapé e nas laterais, igual à roletinha: a folha
// ocupa a largura toda até o teto de 480px (mesmo teto do .rlt-screen).

// Toda a peça é dimensionada a partir da largura do painel — 382px no Figma —
// então ela escala inteira, sem breakpoint. `--dc-u` vale 1px no tamanho de
// design e é a unidade usada aqui e no DayCell.
.dc-frame {
  --dc-panel-w: min(480px, 100vw);
  // Default de 5 colunas (Figma); o componente sobrescreve inline.
  --dc-cell: calc((var(--dc-panel-w) - 32px) / 5.3692);
  --dc-gap: calc(var(--dc-cell) * 6 / 65);
  --dc-u: calc(var(--dc-cell) / 65);

  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  width: var(--dc-panel-w);
  max-height: 100%;
  margin: 0 auto;
  // Reserva a parte do logo que fica acima do painel.
  padding-top: calc(64 * var(--dc-u));
}

// A proporção sai do arquivo, não de um número do Figma: o asset é exportado
// com recorte justo (434x296), então qualquer descasamento aqui distorceria a
// arte. `object-fit: contain` é o cinto de segurança pra quando a arte for
// trocada de novo e a proporção mudar antes deste valor acompanhar.
.dc-logo {
  position: absolute;
  z-index: 2;
  top: 0;
  left: 50%;
  width: calc(217 * var(--dc-u));
  transform: translateX(-50%);
  aspect-ratio: 434 / 296;
  object-fit: contain;
  pointer-events: none;
}

.dc-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  // O padding de cima tem que limpar a parte do logo que invade o painel: o
  // logo tem 148u de altura (217u de largura na proporção do asset) e 64u
  // ficam acima do painel, sobrando 84u de sobreposição + 7u de folga.
  padding: calc(91 * var(--dc-u)) 16px
    calc(env(safe-area-inset-bottom, 0px) + 16px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  // Colado na base: só os cantos de cima arredondam e a borda de baixo some.
  border-bottom: 0;
  border-radius: 16px 16px 0 0;
  background: linear-gradient(180deg, #00005c 0%, #00001b 100%);
  // `clip` e não `hidden`: `hidden` é scroll container e o ajuste de foco do
  // clique desloca o painel (mesmo bug das bordas da roletinha).
  overflow: clip;
}

/* ---------- botões do topo ---------- */
// Assimetria do Figma preservada: (i) a 10px da esquerda, X a 21px da direita.
// z acima do scrim, pra o (i) seguir funcionando como toggle com o popover
// aberto (mesma ordem do topbar/scrim do dropdown ⋮ da roletinha).
.dc-icon {
  position: absolute;
  z-index: 5;
  top: calc(9 * var(--dc-u));
  width: calc(30 * var(--dc-u));
  height: calc(30 * var(--dc-u));

  img {
    @apply w-full h-full;
  }
}

.dc-icon--info {
  left: calc(10 * var(--dc-u));
}

.dc-icon--close {
  right: calc(21 * var(--dc-u));
}

// Scrim transparente que captura o clique-fora pra fechar o popover.
.dc-info-scrim {
  position: absolute;
  z-index: 4;
  inset: 0;
}

// Ancorado logo abaixo do (i): 9 (topo do botão) + 30 (altura) + 8 de folga.
// Mais larga e mais alta do que a caixinha de antes: agora o conteúdo é
// regulamento do CMS, de tamanho imprevisível, e não uma frase nossa. O teto de
// altura vale pra caixa não cobrir a trilha inteira; quem rola é o corpo.
.dc-info {
  position: absolute;
  z-index: 6;
  top: calc(47 * var(--dc-u));
  left: calc(10 * var(--dc-u));
  display: flex;
  flex-direction: column;
  width: calc(300 * var(--dc-u));
  max-height: calc(300 * var(--dc-u));
  padding: 10px;
  border: 1px solid #203570;
  border-radius: 10px;
  background: #0c1b44;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);

  // Barriguinha apontando pro (i): quadradinho girado 45° com só as bordas
  // top/left, centrado sob o botão. O centro do (i) fica em 25*u e o popover
  // começa em 10*u, então o deslocamento é 15*u menos metade do quadrado.
  &::before {
    content: '';

    position: absolute;
    top: -6px;
    left: calc(15 * var(--dc-u) - 6px);
    width: 12px;
    height: 12px;
    border-top: 1px solid #203570;
    border-left: 1px solid #203570;
    border-radius: 3px 0 0 0;
    background: #0c1b44;
    transform: rotate(45deg);
  }
}

// O único scroll container da caixa. `min-height: 0` porque item de flex não
// encolhe abaixo do próprio conteúdo sem isso — e sem encolher, nada rola.
.dc-info__text {
  flex: 1;
  min-height: 0;
  color: rgba(255, 255, 255, 0.85);
  font-size: 11px;
  line-height: 1.4;
  overflow-y: auto;
  // Não encadeia o scroll pro grid da trilha atrás.
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;

  // Vindo HTML do CMS: o preflight do Tailwind zera margem de parágrafo e
  // marcador de lista, então o espaçamento tem que ser declarado aqui. `:deep`
  // porque conteúdo de v-html não recebe o atributo do escopo.
  :deep(p + p),
  :deep(ul),
  :deep(ol) {
    margin-top: 6px;
  }

  :deep(ul),
  :deep(ol) {
    padding-left: 14px;
  }

  :deep(ul) {
    list-style: disc;
  }

  :deep(ol) {
    list-style: decimal;
  }

  :deep(li + li) {
    margin-top: 3px;
  }

  :deep(strong),
  :deep(b) {
    color: #fff;
    font-weight: 700;
  }

  :deep(a) {
    color: #7aa2ff;
    text-decoration: underline;
  }
}

// Texto puro: as quebras de linha do CMS são o único parágrafo que existe.
.dc-info__text--plain {
  white-space: pre-line;
}

/* ---------- estado do jogador ---------- */
.dc-status {
  @apply shrink-0 text-center;

  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 11px;
  font-weight: 700;

  strong {
    color: #fff;
    font-variant-numeric: tabular-nums;
  }
}

/* ---------- trilha ---------- */
// Grid (e não flex-wrap): com flex, a linha só fecha se `N*célula + gaps`
// bater exatamente na largura, e o arredondamento sub-pixel jogava a última
// célula pra linha de baixo. Os degraus em destaque viram `span 2`.

// `minmax(0, 1fr)` e não `1fr`: o mínimo automático do `1fr` é o min-content
// das células, e a palavra "RESGATADO" empurrava as trilhas pra além da
// largura do grid. Com o mínimo zerado a trilha é puramente proporcional.
// A folga de 12u é pro anel de "hoje", que é desenhado FORA da célula: o grid
// rola, e todo scroll container corta o que passa da borda, então o brilho das
// células da primeira/última coluna e linha sumiria. Margem negativa devolve a
// largura que o padding tirou, e as células ficam exatamente do mesmo tamanho.
.dc-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  align-content: flex-start;
  gap: var(--dc-gap);
  min-height: 0;
  margin: calc(-12 * var(--dc-u));
  padding: calc(12 * var(--dc-u));
  overflow-y: auto;
  overflow-x: hidden;
  // Não encadeia o scroll pro fundo (sem rubber-band vazando pro body).
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.dc-state {
  @apply flex flex-col items-center gap-3 text-white/90 text-sm text-center;

  padding: 48px 0;

  &__hint {
    @apply text-white/60 text-xs;
  }
}

.dc-retry {
  @apply text-white text-sm underline;
}

// Desktop: card centrado na largura do Figma, em vez do bottom-sheet do
// mobile (mesma virada que a roletinha faz no .rlt-screen).
@media (min-width: 1024px) {
  .dc-overlay {
    @apply justify-center;

    padding: 12px;
  }

  .dc-frame {
    --dc-panel-w: 382px;
  }

  .dc-panel {
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
  }
}
</style>
